import { injectable } from 'inversify';

import { t } from '@vk-hr-tek/core/translations';

import {
  CustomDropdownItem,
  CustomLinkItem,
  CustomSectionItem,
  GetSideMenuResponse,
  SystemDropdownItem,
  SystemLinkItem,
  SystemSectionItem,
  UserSectionItem,
} from '@app/gen/side_menu';
import { modules } from '@app/modules';
import { UserRoleEnum } from '@app/types';

import {
  ProcessedCustomLinkItem,
  ProcessedCustomSectionItem,
  ProcessedSystemLinkItem,
  ProcessedSystemSectionItem,
  ProcessedUserSectionItem,
  ProcessedChildMenuItem,
  SideMenuEntity,
  SideMenuEntityNicks,
  ProcessedCustomDropdownItem,
  ProcessedSystemDropdownItem,
} from '../types';
import { SystemIconName, CustomIconName } from '../constants';

const MAPPED_MENU_ITEM = (): {
  [key in SideMenuEntityNicks]: {
    title: string;
    href?: string;
  };
} => ({
  main: {
    title: t('modules.Dashboard.dashboard.companyTitle'),
    href: modules['main'].link,
  },
  news: {
    title: t('modules.News.news.companyTitle'),
    href: modules['news'].link,
  },
  process_catalog: {
    title: t('modules.ProcessCatalog.process-catalog.companyTitle'),
    href: modules['process_catalog'].link,
  },
  surveys: {
    title: t('modules.Surveys.surveys.companyTitle'),
    href: modules['surveys'].link,
  },
  company_target_audience: {
    title: t('modules.TargetAudience.target-audience.companyTitle'),
    href: modules['target_audience'].link,
  },
  community: {
    title: t('modules.Community.community.companyTitle'),
    href: modules['community'].link,
  },
  feed: {
    title: t('modules.Feed.feed.companyTitle'),
    href: modules['feed'].link,
  },
  structure: {
    title: t('modules.Organization.organization.companyTitle'),
    href: modules['structure'].link,
  },
  employee_personal: {
    title: t('modules.Personal.personal.employeeTitle'),
    href: modules['personal'].link,
  },
  employee_events: {
    title: t('modules.Events.events.employeeTitle'),
    href: `/${UserRoleEnum.Employee}${modules['events'].link}`,
  },
  employee_policy: {
    title: t('modules.Policy.policy.employeeTitle'),
    href: `/${UserRoleEnum.Employee}${modules['policy'].link}`,
  },
  employee_absences: {
    title: t('modules.Absences.absences.employeeTitle'),
    href: `/${UserRoleEnum.Employee}${modules['absences'].link}`,
  },
  employee_payslips: {
    title: t('modules.Payslips.payslips.employeeTitle'),
    href: `${modules['payslips'].link}`,
  },
  employee_vacations: {
    title: t('modules.Vacations.vacations.employeeTitle'),
    href: `/${UserRoleEnum.Employee}${modules['vacations'].link}`,
  },
  employee_competencies: {
    title: t('modules.Competencies.competencies.employeeTitle'),
    href: `${modules['competencies'].link}`,
  },
  employee_review: {
    title: t('modules.Review.review.employeeTitle'),
    href: `/${UserRoleEnum.Employee}${modules['review'].link}`,
  },
  company_events: {
    title: t('modules.Events..companyTitle'),
    href: `/${UserRoleEnum.Company}${modules['company_events'].link}`,
  },
  company_events_paper: {
    title: t('modules.Events.paper.companyTitle'),
    href: modules['paper'].link,
  },
  company_policy: {
    title: t('modules.Policy.policy.companyTitle'),
    href: `/${UserRoleEnum.Company}${modules['policy'].link}`,
  },
  company_absences: {
    title: t('modules.Absences.absences.companyTitle'),
    href: `/${UserRoleEnum.Company}${modules['absences'].link}`,
  },
  company_vacations: {
    title: t('modules.Vacations.vacations.companyTitle'),
    href: `/${UserRoleEnum.Company}${modules['vacations'].link}`,
  },
  company_employees: {
    title: t('modules.Employees.employees.companyTitle'),
    href: modules['employees'].link,
  },
  company_candidates: {
    title: t('modules.Candidates.candidates.companyTitle'),
    href: modules['candidates'].link,
  },
  company_review: {
    title: t('modules.Review.review.companyTitle'),
    href: `/${UserRoleEnum.Company}${modules['review'].link}`,
  },
  company_social_news: {
    title: t('modules.News.news.companyTitle'),
    href: modules['social_news'].link,
  },
  company_social_quiz: {
    title: t('modules.Quiz.quiz.companyTitle'),
    href: modules['quiz'].link,
  },
  company_social_categories: {
    title: t('modules.Categories.social-categories.companyTitle'),
    href: modules['social_categories'].link,
  },
  company_social_access: {
    title: t('modules.SocialAccess.social-access.companyTitle'),
    href: modules['social_access'].link,
  },
  company_settings: {
    title: t('modules.Settings.company.companyTitle'),
    href: modules['settings'].link,
  },
  company_settings_widgets_content: {
    title: t('modules.Settings.widgets-content.companyTitle'),
    href: modules['widgets_content'].link,
  },
  company_settings_event_types: {
    title: t('modules.Settings..companyTitle'),
    href: modules['event_types'].link,
  },
  company_settings_templates: {
    title: t('modules.Settings.templates.companyTitle'),
    href: modules['templates'].link,
  },
  company_settings_groups: {
    title: t('modules.Settings.groups.companyTitle'),
    href: modules['groups'].link,
  },
  company_settings_profiles: {
    title: t('modules.Settings.profiles.companyTitle'),
    href: modules['profiles'].link,
  },
  company_settings_substitutes: {
    title: t('modules.Settings.substitutes.companyTitle'),
    href: modules['substitutes'].link,
  },
  company_settings_vacations: {
    title: t('modules.Settings.vacations.companyTitle'),
    href: modules['settings_vacations'].link,
  },
  company_settings_attorneys: {
    title: t('modules.Settings.attorneys.companyTitle'),
    href: modules['attorneys'].link,
  },
  company_settings_attorneys_users: {
    title: t('modules.Settings.attorneys-users.companyTitle'),
    href: modules['attorneys_users'].link,
  },
  company_settings_sign_types: {
    title: t('modules.Settings.sign-types.companyTitle'),
    href: modules['sign_types'].link,
  },
  company_settings_account: {
    title: t('modules.Settings.account.companyTitle'),
    href: modules['account'].link,
  },
  dropdown_company_events: {
    title: t('modules.Events.events.companyTitle'),
  },
  dropdown_company_settings: {
    title: t('modules.Settings.settings.companyTitle'),
  },
  dropdown_social_services: {
    title: t('modules.SocialServices.social-services.companyTitle'),
  },
  dropdown_social_life: {
    title: t('modules.SocialLife.social-life.companyTitle'),
  },
  company_services: {
    title: t('dashboard.constants.companyServices'),
  },
  employee_services: { title: t('dashboard.constants.employeeServices') },
  favorites: {
    title: t('modules.Favorites.favorites.companyTitle'),
    href: `${modules['favorites'].link}`,
  },
  company_business_trips: {
    title: t('modules.BusinessTrips.business-trips.companyTitle'),
    href: `/${UserRoleEnum.Company}${modules['business_trips'].link}`,
  },
  employee_business_trips: {
    title: t('modules.BusinessTrips.business-trips.employeeTitle'),
    href: `/${UserRoleEnum.Employee}${modules['business_trips'].link}`,
  },
});

@injectable()
export class SideMenuMapper {
  private processTitle(sideMenuEntity: SideMenuEntity) {
    if ('nick' in sideMenuEntity) {
      return MAPPED_MENU_ITEM()[sideMenuEntity.nick].title;
    }

    return sideMenuEntity.name;
  }

  private getMenuItemId(item: SideMenuEntity): string {
    if ('nick' in item) return item.nick;
    if (item.type === 'custom_link_item' && item.external_system)
      return item.link;
    return item.name;
  }

  private processChildren(
    children: (
      | SystemDropdownItem
      | CustomDropdownItem
      | SystemLinkItem
      | CustomLinkItem
    )[],
  ): ProcessedChildMenuItem[] {
    return children.map((item) => {
      switch (item.type) {
        case 'custom_link_item':
        case 'link_item':
          return this.processLink(item);
        case 'custom_dropdown_item':
        case 'dropdown_item':
          return this.processDropdown(item);
      }
    });
  }

  private processLink(
    item: SystemLinkItem | CustomLinkItem,
  ): ProcessedCustomLinkItem | ProcessedSystemLinkItem {
    if (item.type === 'custom_link_item') {
      return {
        ...item,
        id: this.getMenuItemId(item),
        title: this.processTitle(item),
        href: item.link,
        iconName: CustomIconName[item.icon],
      };
    }

    return {
      ...item,
      id: this.getMenuItemId(item),
      title: this.processTitle(item),
      href: MAPPED_MENU_ITEM()[item.nick].href || '',
      iconName: SystemIconName[item.nick],
    };
  }

  private processSection(
    item: CustomSectionItem | SystemSectionItem,
  ): ProcessedCustomSectionItem | ProcessedSystemSectionItem {
    return {
      ...item,
      id: this.getMenuItemId(item),
      title: this.processTitle(item),
      children: this.processChildren(item.children),
    };
  }

  private processDropdown(
    item: SystemDropdownItem | CustomDropdownItem,
  ): ProcessedCustomDropdownItem | ProcessedSystemDropdownItem {
    if (item.type === 'dropdown_item') {
      return {
        ...item,
        id: this.getMenuItemId(item),
        title: this.processTitle(item),
        children: this.processChildren(item.children),
        iconName: SystemIconName[item.nick],
      };
    }

    return {
      ...item,
      id: this.getMenuItemId(item),
      title: this.processTitle(item),
      children: this.processChildren(item.children),
      iconName: CustomIconName[item.icon],
    };
  }

  private processUserSection(item: UserSectionItem): ProcessedUserSectionItem {
    return {
      ...item,
      id: this.getMenuItemId(item),
      title: this.processTitle(item),
      children: item.children.map(
        (child) => this.processLink(child) as ProcessedCustomLinkItem,
      ),
    };
  }

  processSideMenu(sideMenu: GetSideMenuResponse['menu']) {
    return sideMenu.map((item) => {
      switch (item.type) {
        case 'link_item':
        case 'custom_link_item':
          return this.processLink(item);
        case 'section_item':
        case 'custom_section_item':
          return this.processSection(item);
        case 'dropdown_item':
        case 'custom_dropdown_item':
          return this.processDropdown(item);
        case 'user_section_item':
          return this.processUserSection(item);
      }
    });
  }
}
